import { memo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/useAuth';

/**
 * Routes where PWA UI features (install prompt, offline indicator) should appear.
 * Login is always allowed (even unauthenticated) so users see offline indicator.
 * Authenticated portal routes for students, parents, and teachers are allowed.
 */
const ALLOWED_PREFIXES = ['/student', '/parent', '/teacher', '/dashboard'];

function isAllowedRoute(pathname: string): boolean {
  if (pathname === '/login') return true;
  return ALLOWED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

interface PwaGateProps {
  children: ReactNode;
}

/**
 * Gate component that only renders PWA UI features (install prompt, offline indicator)
 * on login and authenticated portal routes (student, parent, teacher, dashboard).
 *
 * Excludes admin routes (site builder, system settings) and public pages
 * (meet-and-greet, register, invitation, etc.) from PWA UI features.
 *
 * Push notifications are not affected — they use a separate service worker.
 */
function PwaGate({ children }: PwaGateProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { pathname } = useLocation();

  // While auth is loading, don't render PWA UI
  if (isLoading) return null;

  // Login page always gets PWA UI (offline indicator useful there)
  if (pathname === '/login') {
    return <>{children}</>;
  }

  // Authenticated users on allowed portal routes get PWA UI
  if (isAuthenticated && isAllowedRoute(pathname)) {
    return <>{children}</>;
  }

  return null;
}

export default memo(PwaGate);
